import {
  DataTablePrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import { useBankListFromAfterbanks } from "api/Afterbanks.hook"
import { GetTarjetasBancariasByFiltersDtoProps } from "api/DTOs/GetTarjetasBancariasByFiltersDto"
import {
  GetTarjetaBancariaQueryParamsProps,
  deleteTarjetaBancaria,
  getTarjetaBancaria,
} from "api/TarjetaBancaria.service"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import axios from "axios"
import { ModalConfirmDelete } from "common/components/ModalConfirmDelete/ModalConfirmDelete"
import { STATUSCORRECT } from "common/components/ModalVerDocumento/constants"
import notification from "common/utils/notification"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import { useFilterMenu } from "hooks/useFilterMenu"
import { DataTableRowClickEventParams } from "primereact/datatable"
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import { AfterBanksModal } from "../../components/AfterBanksForm/src"
import {
  ModalEditarTarjeta,
  ModalEditarTarjetasProps,
} from "../../components/ModalEditarTarjeta/ModalEditarTarjeta"
import { TarjetaBancariaProps } from "./tarjetas.adapter"
import "./tarjetas.styles.css"
import { TARJETAS_COLUMNS } from "./tarjetasColumns"

export const Tarjetas = ({
  addCardModal,
  setAddCardModal,
  keywordState,
  dtRef,
  pagination,
  setPagination,
}: {
  addCardModal: boolean
  setAddCardModal: React.Dispatch<React.SetStateAction<boolean>>
  keywordState: [
    string | undefined,
    React.Dispatch<React.SetStateAction<string | undefined>>
  ]
  dtRef: RefObject<DataTablePrimeReact<GetTarjetasBancariasByFiltersDtoProps[]>>
  pagination: {
    itemsPerPage: number
    skipCount: number
  }
  setPagination: Dispatch<
    SetStateAction<{
      itemsPerPage: number
      skipCount: number
    }>
  >
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const [idToDelete, setIdToDelete] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const { bankList } = useBankListFromAfterbanks()
  const [keyword] = keywordState
  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: null,
    property: undefined,
  })
  const [modalEditarTarjeta, setModalEditarTarjeta] = useState<
    ModalEditarTarjetasProps["data"]
  >({
    isOpen: false,
    logo: null,
    id: undefined,
    numero: null,
    nombre: undefined,
  })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)
  const onHideModalEditar = () => {
    setModalEditarTarjeta((prev) => ({
      ...prev,
      isOpen: false,
    }))
  }

  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
  }
  const tarjetasCancelToken = useRef(axios.CancelToken.source())
  const fetchTarjetas = useCallback(
    () => (queryParams: GetTarjetaBancariaQueryParamsProps) => {
      tarjetasCancelToken.current?.cancel()
      tarjetasCancelToken.current = axios.CancelToken.source()
      return getTarjetaBancaria(
        {
          ...queryParams,
          ...((queryParams?.Keyword || keyword) && {
            Keyword: queryParams?.Keyword ?? keyword,
          }),
          SkipCount: queryParams?.SkipCount ?? pagination?.skipCount,
          MaxResultCount:
            queryParams?.MaxResultCount ?? pagination?.itemsPerPage,
          SortingCriteria: sortingCriteria?.sort
            ? `${sortingCriteria?.property} ${sortingCriteria?.sort}`
            : undefined,
        },
        tarjetasCancelToken.current.token
      )
    },
    [
      keyword,
      pagination?.skipCount,
      pagination?.itemsPerPage,
      sortingCriteria?.sort,
      sortingCriteria?.property,
    ]
  )

  const { items, isLoading, totalCount, fetchTableData } = useFilterMenu({
    serviceCall: fetchTarjetas,
  })

  const itemsWithLogo = items.map((item: TarjetaBancariaProps) => {
    const bank = bankList?.find((bank) => bank?.service === item?.service)
    return {
      ...item,
      ...(!item["EntidadBancaria.Nombre"] && {
        "EntidadBancaria.Nombre": bank?.fullname,
      }),
      logo: bank?.image,
    }
  })

  const handleRedirectToDetail = (id: string) => {
    history.push(`${ROUTES.TARJETAS}/${id}`)
  }

  const handleDeleteCard = async () => {
    if (isDeleting) return
    try {
      setIsDeleting(true)
      if (!idToDelete) return
      await deleteTarjetaBancaria(idToDelete).then((resp) => {
        if (resp.status === STATUSCORRECT) {
          notification.success({
            body: `${t("notification.deleteCardSuccess")}`,
          })
          setIdToDelete(null)
          fetchTableData()
        }
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    } finally {
      setIsDeleting(false)
    }
  }
  const handleAbortDeleteCard = () => {
    if (isDeleting) return
    setIdToDelete(null)
    setIsDeleting(false)
  }

  return (
    <>
      <div
        id='contenedor-tabla-tarjetas-bancarias'
        className='tw-flex tw-flex-col tw-order-2 tw-overflow-y-hidden tw-max-h-screen tw-w-full tw-max-w-screen tw-h-full'
      >
        <div className='tw-h-full'>
          <div className='tw-h-[calc(100%-60px)]'>
            <TableERP
              refTable={dtRef}
              data-testid='tabla-tarjetas-bancarias'
              id='tabla-tarjetas-bancarias'
              dataKey='idExterno'
              className='tw-h-full'
              onRowClick={({ data }: DataTableRowClickEventParams) => {
                if (!data.id) return
                handleRedirectToDetail(data.id)
              }}
              columns={TARJETAS_COLUMNS({
                t,
                setIdToDelete,
                setModalEditarTarjeta,
              })}
              onSort={onSort}
              sortField={sortField}
              sortOrder={sortOrder}
              value={itemsWithLogo}
              loading={isLoading}
              removableSort
              lazy
              showGridlines
              scrollable
              emptyMessage={t("primeReact.table.emptyMessage")}
            />
          </div>
          <PaginationERP
            className={PAGINATION_STYLES}
            totalRecords={totalCount}
            first={pagination.skipCount}
            rows={pagination.itemsPerPage}
            resetScroll={() => dtRef.current && dtRef.current?.resetScroll()}
            setPagination={setPagination}
            optionDropdown={[10, 20, 50, 100]}
            texts={{
              textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
              textCurrentPageReport: `${t("pagination.from")}`,
            }}
          />
        </div>
      </div>

      <AfterBanksModal
        bankList={bankList ?? []}
        isOpen={addCardModal}
        onClose={() => {
          setAddCardModal(false)
          fetchTableData()
        }}
      />

      <ModalConfirmDelete
        isOpen={Boolean(idToDelete)}
        title={t("bancos.cardsTable.deleteModal.title")}
        paragraph={t("bancos.cardsTable.deleteModal.msg")}
        onConfirm={handleDeleteCard}
        onCancel={handleAbortDeleteCard}
        isLoading={isDeleting}
      />

      <ModalEditarTarjeta
        data={modalEditarTarjeta}
        onHide={onHideModalEditar}
        updateTable={fetchTableData}
      />
    </>
  )
}
